import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { IconLink3Fill18, IconSliders3Fill18 } from "nucleo-ui-fill-18"
import {
	IconEyeOutline18 as Eye,
	IconEyeSlashOutline18 as EyeOff,
} from "nucleo-ui-outline-18"

import type { ContactFormField } from "@productlane/lib/src/contact-form-fields"
import type { WidgetPreviewSettings } from "@productlane/lib/src/widget-preview-message"
import { cn, scale } from "@productlane/lib/src/browser"
import { parseContactFormFields } from "@productlane/lib/src/contact-form-fields"
import { WIDGET_LINK_URL_HINT } from "@productlane/lib/src/widget-link-url"
import {
	Button,
	CopyButton,
	Form,
	FormInput,
	FormSwitch,
	Switch,
	Tooltip,
	WidgetIconTypeRecord,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { SnippetOptions } from "./install-snippet-data"
import type { WidgetSettings } from "./widget-settings-schema"
import { NucleoFillIconPicker } from "@/components/nucleo-fill-icon-picker"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useRequiredPlans } from "@/lib/plans"
import { useSavableForm } from "@/lib/useSavableForm"
import { useTrial } from "@/lib/useTrial"
import { mutateOk, useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../settings-header"
import { SettingsRightRail } from "../settings-right-rail"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "../settings-switches/settings-container"
import { WidgetPreview } from "../widget-preview"
import { ContactAvatarsSection } from "./contact-avatars-section"
import { ContactFormFieldsRow, showsFormFieldsRow } from "./contact-form-fields"
import { AGENT_SETTINGS_HREF, ConversationGroup } from "./conversation-group"
import { InstallSnippetHeader } from "./install-snippet-header"
import { isInstallSnippetOpen } from "./install-snippet-open"
import { InstallSnippets } from "./install-snippets"
import {
	invalidWidgetFieldToast,
	invalidWidgetLinkToast,
	saveWidgetSettingsForm,
} from "./save-widget-settings"
import { ThemeSection } from "./theme-section"
import { WidgetSettingsSchema, WidgetView } from "./widget-settings-schema"

const WIDGET_ICON_OPTIONS = [
	{
		value: "NONE" as const,
		label: "None: Build your own icon",
		Icon: WidgetIconTypeRecord.NONE,
	},
	{
		value: "CHAT" as const,
		label: "Chat: Recommended for homepages",
		Icon: WidgetIconTypeRecord.CHAT,
	},
	{
		value: "QUESTION_MARK" as const,
		label: "Question mark: Recommended for web apps",
		Icon: WidgetIconTypeRecord.QUESTION_MARK,
	},
] as const

const WIDGET_SIZE_OPTIONS = [
	{
		value: "SMALL",
		label: "Small - 32px, fits a compact toolbar",
		launcherPx: 32,
		px: 16,
		fontPx: 7,
	},
	{
		value: "MEDIUM",
		label: "Medium - 40px, the default",
		launcherPx: 40,
		px: 22,
		fontPx: 9,
	},
	{
		value: "LARGE",
		label: "Large - 48px, hard to miss on a homepage",
		launcherPx: 48,
		px: 28,
		fontPx: 11,
	},
] as const

type WidgetSwitchField =
	| "isRoadmapEnabled"
	| "isChangelogEnabled"
	| "isWhatsNewEnabled"
	| "isSupportEnabled"
	| "isHelpCenterEnabled"

export function WidgetSettingsPage() {
	const decodedToken = useAuthData()
	const navigate = useNavigate()
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { passes: hasScalePlan } = useRequiredPlans(scale.andUp)
	const { isTrial, isStartupProgram } = useTrial()

	const [widgetSettings] = useQuery(queries.widgetSettings())
	const [widgetLinks] = useQuery(queries.widgetLinks())

	const toastedInvalidLinks = useRef(new Set<string>())

	const { form } = useSavableForm<WidgetSettings>({
		schema: WidgetSettingsSchema,
		mode: "onBlur",
		defaultValues: {
			id: widgetSettings?.id,

			hasCustomLinksEnabled: widgetSettings?.hasCustomLinksEnabled,
			isChangelogEnabled: widgetSettings?.isChangelogEnabled,
			isRoadmapEnabled: widgetSettings?.isRoadmapEnabled,
			isBrandingEnabled: widgetSettings?.isBrandingEnabled,
			isWhatsNewEnabled: widgetSettings?.isWhatsNewEnabled,
			isSupportEnabled: widgetSettings?.isSupportEnabled,
			isHelpCenterEnabled: widgetSettings?.isHelpCenterEnabled,
			isWidgetIconVisible: widgetSettings?.isWidgetIconVisible,
			widgetIconType: widgetSettings?.widgetIconType,
			widgetSize: widgetSettings?.widgetSize ?? "MEDIUM",
			isPortalAuthEnabled: widgetSettings?.isPortalAuthEnabled,
			isGuestChatEnabled: widgetSettings?.isGuestChatEnabled ?? true,
			isAiFirstEnabled: widgetSettings?.isAiFirstEnabled ?? false,
			isContactAvatarsEnabled: widgetSettings?.isContactAvatarsEnabled ?? true,
			widgetLinks: widgetLinks
				.map((widgetLink) => ({
					id: widgetLink.id,
					workspaceId: widgetLink.workspaceId,
					title: widgetLink.title ?? "",
					url: widgetLink.url ?? "",
					icon: widgetLink.icon ?? "",
				}))
				.sort((a, b) => a.id.localeCompare(b.id)),
			defaultView: widgetSettings?.defaultView,
			isFeedbackEnabled: widgetSettings?.isFeedbackEnabled,
			isLiveChatEnabled: widgetSettings?.isLiveChatEnabled,

			workspaceId: widgetSettings?.workspaceId,
		},
		saveFunc: async (values) => {
			const { invalidLinks } = await saveWidgetSettingsForm({
				settings: {
					id: values.id,
					workspaceId: values.workspaceId,
					hasCustomLinksEnabled: values.hasCustomLinksEnabled,
					isChangelogEnabled: values.isChangelogEnabled,
					isRoadmapEnabled: values.isRoadmapEnabled,
					isSupportEnabled: values.isSupportEnabled,
					isHelpCenterEnabled: values.isHelpCenterEnabled,
					isLiveChatEnabled: values.isLiveChatEnabled,
					isBrandingEnabled: values.isBrandingEnabled,
					isFeedbackEnabled: values.isFeedbackEnabled,
					isWhatsNewEnabled: values.isWhatsNewEnabled,
					defaultView: (values.defaultView ?? "") as keyof typeof WidgetView,
					isEnabled: true,
					isWidgetIconVisible: values.widgetIconType !== "NONE",
					widgetIconType: values.widgetIconType,
					widgetSize: values.widgetSize,
					isPortalAuthEnabled: values.isPortalAuthEnabled,
					isGuestChatEnabled: values.isGuestChatEnabled,
					isAiFirstEnabled: values.isAiFirstEnabled,
					isContactAvatarsEnabled: values.isContactAvatarsEnabled,
				},
				formLinks: values.widgetLinks ?? [],
				storedLinks: widgetLinks,
				updateSettings: (args) =>
					mutateOk(zero.mutate(mutators.widgetSettings.update(args))),
				updateLink: (args) =>
					mutateOk(zero.mutate(mutators.widgetLinks.update(args))),
			})
			for (const invalid of invalidLinks) {
				form.setError(`widgetLinks.${invalid.index}.url`, {
					message: WIDGET_LINK_URL_HINT,
				})
				const toastKey = `${invalid.link.id}:${invalid.link.url ?? ""}`
				if (toastedInvalidLinks.current.has(toastKey)) continue
				toastedInvalidLinks.current.add(toastKey)
				toast.info({
					...invalidWidgetLinkToast(invalid),
					id: `widget-link-url-${invalid.link.id}`,
					icon: IconLink3Fill18,
				})
			}
			return values
		},
		onInvalid: (errors) => {
			toast.info({
				...invalidWidgetFieldToast(errors),
				id: "widget-settings-invalid",
				icon: IconSliders3Fill18,
			})
		},
		onFail: (_, error) => {
			console.error("Failed to update widget settings", error)
			captureError(error, {
				domain: "portal",
				action: "update widget settings",
				workspaceId: workspace?.id,
				userId: decodedToken.userId,
			})
			toast.error({ title: "Failed to save widget settings" })
		},
		autoSave: true,
		autoSaveDelay: 1000,
	})

	// Reading formState at render level is what subscribes the component to it.
	const isFormDirty = form.formState.isDirty

	// Update form values when Zero data loads
	useEffect(() => {
		// Zero hands back a fresh object on every sync tick, so an unrelated row
		// change used to reset the form mid-edit and drop the pending toggle.
		if (isFormDirty) return
		if (widgetSettings) {
			const formData: WidgetSettings = {
				id: widgetSettings.id,
				workspaceId: widgetSettings.workspaceId,
				hasCustomLinksEnabled: widgetSettings?.hasCustomLinksEnabled ?? false,
				isChangelogEnabled: widgetSettings?.isChangelogEnabled ?? false,
				isRoadmapEnabled: widgetSettings?.isRoadmapEnabled ?? false,
				isBrandingEnabled: widgetSettings?.isBrandingEnabled ?? false,
				isWhatsNewEnabled: widgetSettings?.isWhatsNewEnabled ?? false,
				isSupportEnabled: widgetSettings?.isSupportEnabled ?? false,
				isHelpCenterEnabled: widgetSettings?.isHelpCenterEnabled ?? false,
				isWidgetIconVisible: widgetSettings?.isWidgetIconVisible ?? false,
				widgetIconType: widgetSettings?.widgetIconType || "QUESTION_MARK",
				widgetSize: widgetSettings?.widgetSize || "MEDIUM",
				isPortalAuthEnabled: widgetSettings?.isPortalAuthEnabled ?? false,
				isContactAvatarsEnabled:
					widgetSettings?.isContactAvatarsEnabled ?? true,
				widgetLinks: widgetLinks
					.map((widgetLink) => ({
						id: widgetLink.id,
						workspaceId: widgetLink.workspaceId,
						title: widgetLink.title ?? "",
						url: widgetLink.url ?? "",
						icon: widgetLink.icon ?? "",
					}))
					.sort((a, b) => a.id.localeCompare(b.id)),
				defaultView: widgetSettings?.defaultView ?? WidgetView.DEFAULT,
				isFeedbackEnabled: widgetSettings?.isFeedbackEnabled ?? false,
				isLiveChatEnabled: widgetSettings?.isLiveChatEnabled ?? false,
				isGuestChatEnabled: widgetSettings?.isGuestChatEnabled ?? true,
				isAiFirstEnabled: widgetSettings?.isAiFirstEnabled ?? false,
			}

			// Use form.reset directly to bypass useSavableForm's reset restrictions
			form.reset(formData)
		}
	}, [widgetSettings, widgetLinks, form, isFormDirty])

	// A switch writes straight through, so a toggle survives a sync landing
	// inside the debounced form save. Reverts itself when the write fails.
	const persistSwitch = useCallback(
		async (patch: Partial<Record<WidgetSwitchField, boolean>>) => {
			const { id, workspaceId } = form.getValues()
			if (!id || !workspaceId) return
			try {
				await mutateOk(
					zero.mutate(
						mutators.widgetSettings.update({ id, workspaceId, ...patch }),
					),
				)
			} catch (error) {
				for (const [field, value] of Object.entries(patch)) {
					form.setValue(field as WidgetSwitchField, !value)
				}
				captureError(error, {
					domain: "portal",
					action: "update widget settings",
					workspaceId: workspace?.id,
					userId: decodedToken.userId,
					extra: { fields: Object.keys(patch) },
				})
				toast.error({ title: "Failed to save widget settings" })
			}
		},
		[form, zero, workspace?.id, decodedToken.userId],
	)

	const hasCustomLinksEnabledWatch = form.watch("hasCustomLinksEnabled")

	const widgetSettingsExist = !!widgetSettings

	const [snippetToggle, setSnippetToggle] = useState<boolean | null>(null)
	const isSnippetOpen = isInstallSnippetOpen({
		toggled: snippetToggle,
		isTrial,
		hasWidgetSettings: widgetSettingsExist,
	})

	const createWidgetSettingsMutation =
		api.workspace.createWidgetSettings.useMutation({
			onSuccess: () => {
				toast.success({ title: "Widget settings generated" })
			},
			onError: (e) => {
				toast.error({ title: e.message })
			},
		})

	const handleCreateWidgetSettings = () => {
		createWidgetSettingsMutation.mutate({
			workspaceId: decodedToken.workspaceId,
		})
	}

	const createWidgetLinksMutation = api.workspace.createWidgetLinks.useMutation(
		{},
	)

	// Admin-only tRPC read: the secret is not part of the synced widget row.
	const signingSecretQuery = api.workspace.widgetSigningSecret.useQuery(
		undefined,
		{ retry: false },
	)
	const signingSecret = signingSecretQuery.data?.secret ?? ""

	// Only a new key or a theme change needs the iframe back from the server;
	// everything else reaches the preview through liveSettings below.
	const previewSettingsHash = useMemo(
		() =>
			JSON.stringify({
				widgetKey: widgetSettings?.widgetKey,
				accentColor: workspace?.accentColor,
				darkModeAccentColor: workspace?.darkModeAccentColor,
				buttonTextColor: workspace?.buttonTextColor,
				darkModeButtonTextColor: workspace?.darkModeButtonTextColor,
			}),
		[widgetSettings?.widgetKey, workspace],
	)

	const storedContactFormFields = useMemo(
		() => parseContactFormFields(widgetSettings?.contactFormFields),
		[widgetSettings?.contactFormFields],
	)
	const [liveContactFormFields, setLiveContactFormFields] = useState<
		ContactFormField[] | null
	>(null)

	const formValues = form.watch()
	const previewLiveSettings = useMemo<WidgetPreviewSettings>(
		() => ({
			widgetSettings: {
				isChangelogEnabled: formValues.isChangelogEnabled,
				isRoadmapEnabled: formValues.isRoadmapEnabled,
				isFeedbackEnabled: formValues.isFeedbackEnabled,
				isLiveChatEnabled: formValues.isLiveChatEnabled,
				isGuestChatEnabled: formValues.isGuestChatEnabled,
				isAiFirstEnabled: formValues.isAiFirstEnabled,
				isSupportEnabled: formValues.isSupportEnabled,
				isHelpCenterEnabled: formValues.isHelpCenterEnabled,
				isBrandingEnabled: formValues.isBrandingEnabled,
				isWhatsNewEnabled: formValues.isWhatsNewEnabled,
				isContactAvatarsEnabled: formValues.isContactAvatarsEnabled,
				isWidgetIconVisible: formValues.widgetIconType !== "NONE",
				widgetIconType: formValues.widgetIconType,
				widgetSize: formValues.widgetSize,
				hasCustomLinksEnabled: formValues.hasCustomLinksEnabled,
				contactFormFields: liveContactFormFields ?? storedContactFormFields,
			},
			widgetLinks: (formValues.widgetLinks ?? [])
				.filter((link) => !!link?.title)
				.map((link) => ({
					id: link.id,
					title: link.title,
					url: link.url,
					icon: link.icon,
				})),
		}),
		[formValues, liveContactFormFields, storedContactFormFields],
	)
	const [snippetOptions, setSnippetOptions] = useState<SnippetOptions>({})
	const [isSecretVisible, setIsSecretVisible] = useState(false)
	const [isGeneratingSecret, setIsGeneratingSecret] = useState(false)

	const handleGenerateSigningSecret = async () => {
		if (
			signingSecret &&
			!confirm(
				"This will replace your existing secret and invalidate all existing tokens. Continue?",
			)
		) {
			return
		}

		setIsGeneratingSecret(true)
		try {
			await mutateOk(
				zero.mutate(mutators.widgetSettings.generateSigningSecret()),
				"server",
			)
			await signingSecretQuery.refetch()

			toast.success({
				title: "Signing secret generated",
				description: "Copy and store it securely - it won't be shown again",
			})
		} catch (e) {
			toast.error({
				title: e instanceof Error ? e.message : "Failed to generate secret",
			})
		} finally {
			setIsGeneratingSecret(false)
		}
	}

	return (
		<>
			<SettingsHeader
				title="Widget"
				subtitle="Install a unified widget for your support, feedback and releases."
				docsArticle="ee89cc5f-c71e-4f3c-9d80-009a7f535ef5"
			/>
			<div className="min-w-0">
				<Form className="space-y-0" form={form} onSubmit={() => undefined}>
					<div className="relative flex gap-4">
						<div className="flex flex-col items-center">
							<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-sm font-medium text-secondary shadow-card dark:bg-gray-800 dark:shadow-none">
								1
							</div>
							<div className="w-[0.5px] flex-1 bg-gray-200 dark:bg-gray-700" />
						</div>
						<div className="min-w-0 flex-1 pb-8">
							<InstallSnippetHeader
								isOpen={isSnippetOpen}
								canToggle={widgetSettingsExist}
								onToggle={() => setSnippetToggle(!isSnippetOpen)}
							/>

							{isSnippetOpen && (
								<div className="relative mt-3 flex items-center justify-center">
									{!widgetSettingsExist && (
										<div className="absolute z-10 flex items-center justify-center">
											<Button
												variant="outline"
												onClick={handleCreateWidgetSettings}
												isLoading={createWidgetSettingsMutation.isLoading}
											>
												Generate snippet
											</Button>
										</div>
									)}

									<div
										className={cn("w-full", {
											"pointer-events-none select-none opacity-60 blur-[2px]":
												!widgetSettingsExist,
										})}
									>
										<InstallSnippets
											widgetKey={widgetSettings?.widgetKey}
											options={snippetOptions}
											onOptionsChange={setSnippetOptions}
										/>
									</div>
								</div>
							)}
						</div>
					</div>

					{widgetSettingsExist && (
						<>
							<div className="relative flex gap-4">
								<div className="flex flex-col items-center">
									<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-sm font-medium text-secondary shadow-card dark:bg-gray-800 dark:shadow-none">
										2
									</div>
									<div className="w-[0.5px] flex-1 bg-gray-200 dark:bg-gray-700" />
								</div>
								<div className="min-w-0 flex-1 pb-8">
									<div className="font-semibold text-primary">Authenticate</div>
									<p className="mb-3 text-sm text-secondary">
										Authenticate users on your widget.
									</p>

									<SettingsCardInsetContext.Provider value={false}>
										<SettingsContainer>
											<div>
												{widgetSettings?.identityVerificationEnabled && (
													<>
														<div className="mb-4 border-b pb-4">
															<FormSwitch
																name="isPortalAuthEnabled"
																description="Automatically sign in authenticated widget users when they open portal links."
																label="Portal Authentication"
															/>
														</div>
													</>
												)}

												{signingSecret ? (
													<div className="space-y-3">
														<div className="flex items-center gap-2">
															<div className="flex-1">
																<label
																	htmlFor="signing-secret"
																	className="mb-1 block text-xs font-medium text-secondary"
																>
																	Signing Secret
																</label>
																<div className="flex items-center gap-2">
																	<div className="relative flex-1">
																		<input
																			id="signing-secret"
																			type={
																				isSecretVisible ? "text" : "password"
																			}
																			value={signingSecret}
																			readOnly
																			className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 pr-10 font-mono text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
																		/>
																		<button
																			type="button"
																			onClick={() =>
																				setIsSecretVisible(!isSecretVisible)
																			}
																			className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
																		>
																			{isSecretVisible ? (
																				<EyeOff className="h-4 w-4" />
																			) : (
																				<Eye className="h-4 w-4" />
																			)}
																		</button>
																	</div>
																	<CopyButton
																		value={signingSecret ?? ""}
																		className="border border-gray-200 dark:border-gray-700"
																		onCopied={() =>
																			toast.success({
																				title:
																					"Signing secret copied to clipboard",
																			})
																		}
																	/>
																</div>
															</div>
														</div>
														<div className="text-xs text-tertiary">
															Created on{" "}
															{widgetSettings?.signingSecretCreatedAt
																? new Date(
																		widgetSettings.signingSecretCreatedAt,
																	).toLocaleDateString()
																: "Unknown"}
														</div>
														<Button
															type="button"
															variant="outline"
															size="sm"
															onClick={handleGenerateSigningSecret}
															isLoading={isGeneratingSecret}
														>
															Regenerate Secret
														</Button>
													</div>
												) : (
													<Button
														type="button"
														variant="outline"
														onClick={handleGenerateSigningSecret}
														isLoading={isGeneratingSecret}
													>
														Generate Signing Secret
													</Button>
												)}
											</div>
										</SettingsContainer>
									</SettingsCardInsetContext.Provider>
								</div>
							</div>

							<div className="relative flex gap-4">
								<div className="flex flex-col items-center">
									<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-sm font-medium text-secondary shadow-card dark:bg-gray-800 dark:shadow-none">
										3
									</div>
									<div className="w-[0.5px] flex-1 bg-gray-200 dark:bg-gray-700" />
								</div>
								<div className="min-w-0 flex-1 pb-8">
									<div className="font-semibold text-primary">Customize</div>
									<p className="mb-3 text-sm text-secondary">
										Customize the appearance and behavior of your widget.
									</p>

									<SettingsCardInsetContext.Provider value={false}>
										<SettingsContainer className="space-y-0 divide-y">
											<div className="flex items-center justify-between pb-3">
												<div>
													<div className="text-sm font-medium text-primary">
														Contact options
													</div>
													<div className="mt-1 text-xs text-secondary">
														Choose how customers can reach you.
													</div>
												</div>
												<div className="inline-flex flex-shrink-0 rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
													{(
														[
															{
																label: "None",
																value: "none",
																tooltip:
																	"None: In case you only use e.g. the Changelog",
															},
															{
																label: "Contact form",
																value: "contactForm",
																tooltip:
																	"Contact form: If you have too much volume for Live Chat",
															},
															{
																label: "Live Chat",
																value: "liveChat",
																tooltip:
																	"Live Chat: The most personal support experience",
															},
														] as const
													).map((option) => {
														const isLiveChat = form.watch("isLiveChatEnabled")
														const isFeedback = form.watch("isFeedbackEnabled")
														const currentValue =
															isLiveChat && isFeedback
																? "liveChat"
																: isFeedback
																	? "contactForm"
																	: "none"
														return (
															<Tooltip
																key={option.value}
																label={option.tooltip}
															>
																<button
																	type="button"
																	className={cn(
																		"rounded-[0.45rem] px-3 py-1.5 text-xs font-medium transition-colors",
																		currentValue === option.value
																			? "bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
																			: "text-secondary hover:text-primary",
																	)}
																	onClick={() => {
																		form.setValue(
																			"isLiveChatEnabled",
																			option.value === "liveChat",
																			{ shouldDirty: true },
																		)
																		form.setValue(
																			"isFeedbackEnabled",
																			option.value !== "none",
																			{ shouldDirty: true },
																		)
																	}}
																>
																	{option.label}
																</button>
															</Tooltip>
														)
													})}
												</div>
											</div>
											{widgetSettings &&
												showsFormFieldsRow({
													isLiveChatEnabled: form.watch("isLiveChatEnabled"),
													isFeedbackEnabled: form.watch("isFeedbackEnabled"),
												}) && (
													<ContactFormFieldsRow
														settingsId={widgetSettings.id}
														storedFields={storedContactFormFields}
														onLiveChange={setLiveContactFormFields}
													/>
												)}
											<ConversationGroup
												isGuestChatEnabled={!!form.watch("isGuestChatEnabled")}
												onGuestChatChange={(value) => {
													form.setValue("isGuestChatEnabled", value, {
														shouldDirty: true,
													})
												}}
												showSignInNote={
													!form.watch("isGuestChatEnabled") &&
													!widgetSettings?.identityVerificationEnabled
												}
												isAiFirstEnabled={!!form.watch("isAiFirstEnabled")}
												onAiFirstChange={(value) => {
													form.setValue("isAiFirstEnabled", value, {
														shouldDirty: true,
													})
												}}
												isLiveChatEnabled={!!form.watch("isLiveChatEnabled")}
												isAgentEnabled={!!workspace?.aiChatSettings?.isEnabled}
												onTurnOnLiveChat={() => {
													for (const field of [
														"isLiveChatEnabled",
														"isFeedbackEnabled",
														"isAiFirstEnabled",
													] as const) {
														form.setValue(field, true, { shouldDirty: true })
													}
												}}
												onOpenAgentSettings={() =>
													void navigate({ href: AGENT_SETTINGS_HREF })
												}
											/>
											<div className="py-2.5">
												<FormSwitch
													name="isRoadmapEnabled"
													description="Add a link 'Request a feature' that opens existing requests."
													label="Feature requests"
													onCheckedChange={(checked) => {
														void persistSwitch({ isRoadmapEnabled: checked })
													}}
												/>
											</div>
											<div className="py-2.5">
												<Switch
													name="isChangelogEnabled"
													description="Show your two latest changelogs and send notifications."
													label="Changelog"
													checked={form.watch("isChangelogEnabled")}
													onCheckedChange={(checked) => {
														form.setValue("isChangelogEnabled", checked, {
															shouldDirty: true,
														})
														form.setValue("isWhatsNewEnabled", checked, {
															shouldDirty: true,
														})
														void persistSwitch({
															isChangelogEnabled: checked,
															isWhatsNewEnabled: checked,
														})
													}}
												/>
											</div>
											<div className="py-2.5">
												<FormSwitch
													name="isSupportEnabled"
													description="Link customers to the page where they can see all their requests."
													label="Support Portal"
													onCheckedChange={(checked) => {
														void persistSwitch({ isSupportEnabled: checked })
													}}
												/>
											</div>
											<div className="py-2.5">
												<FormSwitch
													name="isHelpCenterEnabled"
													description="Make help center articles available in your app."
													label="Docs"
													onCheckedChange={(checked) => {
														void persistSwitch({ isHelpCenterEnabled: checked })
													}}
												/>
											</div>
											<div className="flex items-center justify-between py-3">
												<div>
													<p className="text-sm font-medium">Widget size</p>
													<p className="text-xs text-secondary">
														How large the launcher renders on your site.
													</p>
												</div>
												<div className="inline-flex flex-shrink-0 rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
													{WIDGET_SIZE_OPTIONS.map((option) => {
														const currentValue = form.watch("widgetSize")
														return (
															<Tooltip key={option.value} label={option.label}>
																<button
																	type="button"
																	className={cn(
																		"flex items-center justify-center rounded-[0.45rem] p-1.5",
																		currentValue === option.value
																			? "bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
																			: "text-secondary hover:text-primary",
																	)}
																	onClick={() => {
																		form.setValue("widgetSize", option.value, {
																			shouldDirty: true,
																		})
																	}}
																>
																	<div className="flex h-7 w-7 items-center justify-center">
																		<span
																			className="flex items-center justify-center rounded-full bg-current"
																			style={{
																				width: option.px,
																				height: option.px,
																			}}
																		>
																			<span
																				className="font-semibold tabular-nums leading-none text-white dark:text-gray-900"
																				style={{ fontSize: option.fontPx }}
																			>
																				{option.launcherPx}
																			</span>
																		</span>
																	</div>
																</button>
															</Tooltip>
														)
													})}
												</div>
											</div>
											<div className="flex items-center justify-between py-3">
												<div>
													<p className="text-sm font-medium">Widget icon</p>
													<p className="text-xs text-secondary">
														Choose the icon displayed on your widget.
													</p>
												</div>
												<div className="inline-flex flex-shrink-0 rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
													{WIDGET_ICON_OPTIONS.map((option) => {
														const currentValue = form.watch("widgetIconType")
														const Icon = option.Icon
														return (
															<Tooltip key={option.value} label={option.label}>
																<button
																	type="button"
																	className={cn(
																		"flex items-center justify-center rounded-[0.45rem] p-1.5 transition-colors",
																		currentValue === option.value
																			? "bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
																			: "text-secondary hover:text-primary",
																	)}
																	onClick={() => {
																		form.setValue(
																			"widgetIconType",
																			option.value,
																			{
																				shouldDirty: true,
																			},
																		)
																		form.setValue(
																			"isWidgetIconVisible",
																			option.value !== "NONE",
																			{ shouldDirty: true },
																		)
																	}}
																>
																	<div className="flex h-7 w-7 items-center justify-center rounded-full">
																		<Icon />
																	</div>
																</button>
															</Tooltip>
														)
													})}
												</div>
											</div>

											{widgetLinks.length > 0 ? (
												<div className="py-3">
													<FormSwitch
														name="hasCustomLinksEnabled"
														description="Add up to five links to your widget."
														label="Custom links"
													/>
													{hasCustomLinksEnabledWatch && (
														<>
															{(form.watch("widgetLinks") ?? []).map(
																(link, index) => (
																	<div key={index} className="mt-3">
																		<div className="flex flex-col gap-3 sm:flex-row sm:items-start">
																			<div className="flex-shrink-0">
																				<NucleoFillIconPicker
																					icon={link.icon}
																					onChange={(icon) => {
																						form.setValue(
																							`widgetLinks.${index}.icon`,
																							icon,
																							{ shouldDirty: true },
																						)
																					}}
																					buttonClassName="h-8 min-h-8 w-8 bg-gray-100 hover:bg-gray-150 dark:bg-gray-750 dark:hover:bg-gray-700"
																				/>
																			</div>
																			<div className="flex flex-1 flex-col gap-3 sm:flex-row">
																				<FormInput
																					name={`widgetLinks.${index}.title`}
																					placeholder="Title"
																					className="w-full"
																					value={link.title ?? ""}
																					onChange={(e) =>
																						form.setValue(
																							`widgetLinks.${index}.title`,
																							e.target.value,
																						)
																					}
																				/>
																				<FormInput
																					name={`widgetLinks.${index}.url`}
																					placeholder="URL"
																					className="w-full"
																					value={link.url ?? ""}
																					onChange={(e) =>
																						form.setValue(
																							`widgetLinks.${index}.url`,
																							e.target.value,
																						)
																					}
																				/>
																			</div>
																		</div>
																	</div>
																),
															)}
														</>
													)}
												</div>
											) : (
												<div className="py-2.5">
													<Switch
														description="Add up to five links to your widget"
														label="Custom links"
														name="createWidgetLinks"
														onCheckedChange={async () => {
															form.setValue("hasCustomLinksEnabled", true)

															await toast.promise(
																createWidgetLinksMutation.mutateAsync({
																	workspaceId: widgetSettings.workspaceId ?? "",
																}),
																{
																	loading: "Generating widget links",
																	success: "Widget links created successfully",
																	error: "Failed to create widget links",
																},
																"create-widget-links",
															)
														}}
													/>
												</div>
											)}

											<div className="py-3">
												<ContactAvatarsSection
													isContactAvatarsEnabled={
														form.watch("isContactAvatarsEnabled") ?? true
													}
													onToggle={(checked) =>
														form.setValue("isContactAvatarsEnabled", checked, {
															shouldDirty: true,
														})
													}
												/>
											</div>

											<div className="flex items-center justify-between py-3">
												<div className="pr-4">
													<p className="text-sm font-medium">
														Productlane branding
													</p>
													<p className="mt-0.5 text-xs text-secondary">
														Show the &quot;Powered by Productlane&quot; footer
														in your widget.
													</p>
												</div>
												{!hasScalePlan ? (
													<UpgradeButton required={scale.andUp} />
												) : isStartupProgram ? (
													<Tooltip label="The Startup program keeps the Productlane branding. Move to the full Scale plan to remove it.">
														<Switch name="isBrandingEnabled" checked disabled />
													</Tooltip>
												) : isTrial ? (
													<Tooltip label="Productlane branding can only be removed on a paid plan, not during the trial.">
														<Switch name="isBrandingEnabled" checked disabled />
													</Tooltip>
												) : (
													<Switch
														name="isBrandingEnabled"
														checked={form.watch("isBrandingEnabled") ?? true}
														onCheckedChange={(checked) => {
															form.setValue("isBrandingEnabled", checked, {
																shouldDirty: true,
															})
														}}
													/>
												)}
											</div>
										</SettingsContainer>
									</SettingsCardInsetContext.Provider>
								</div>
							</div>

							<div className="relative flex gap-4">
								<div className="flex flex-col items-center">
									<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-sm font-medium text-secondary shadow-card dark:bg-gray-800 dark:shadow-none">
										4
									</div>
								</div>
								<div className="min-w-0 flex-1 pb-8">
									<div className="font-semibold text-primary">Theme</div>
									<p className="mb-3 text-sm text-secondary">
										Match the widget to your brand.
									</p>

									<ThemeSection />
								</div>
							</div>
						</>
					)}
				</Form>
			</div>
			<SettingsRightRail>
				<WidgetPreview
					fill
					widgetKey={widgetSettings?.widgetKey}
					settingsHash={previewSettingsHash}
					liveSettings={previewLiveSettings}
					spotlight={snippetOptions.spotlight}
				/>
			</SettingsRightRail>
		</>
	)
}
